/* custom-product.js — gallery, tooltip, qty, bundle add-to-cart */

(function () {
  'use strict';

  /* ── Gallery thumbnail switching ──────────────────────────────────────────
     The image reacts only once the new file can actually be painted.

     It used to fade the main image to opacity 0 and shrink it the moment you
     clicked, then swap src on a fixed 180ms timer and fade straight back in -
     never checking whether the new image had arrived. On anything slower than a
     warm cache that left a genuinely empty frame where the product had been,
     which reads as a broken page rather than a loading one.

     Now the click marks the thumbnail immediately - instant feedback that costs
     nothing and cannot be wrong - while the new file is fetched off-screen. The
     main image is left alone until the bytes are decoded, so it never blanks. If
     the fetch is slow enough to notice, a spinner appears over the image that is
     still on screen, so there is always something to look at.
  */
  var mainImage    = document.getElementById('cp-main-image');
  var SLOW_LOAD_MS = 140;   /* under this a spinner would only flicker */

  function showMainImage(url) {
    if (!mainImage || !url) return;
    if (mainImage.getAttribute('src') === url) return;   /* already showing it */

    var wrap    = mainImage.closest('.cp-gallery__main');
    var settled = false;
    var slowTimer = setTimeout(function () {
      if (!settled && wrap) wrap.classList.add('is-img-loading');
    }, SLOW_LOAD_MS);

    function finish() {
      settled = true;
      clearTimeout(slowTimer);
      if (wrap) wrap.classList.remove('is-img-loading');
    }

    var pre = new Image();
    pre.decoding = 'async';
    pre.onload = function () {
      finish();
      /*
        Straight swap, no fade.

        There were two earlier attempts at a cross-fade and both could strand the
        image part-way. Driving opacity from requestAnimationFrame fails because
        rAF does not run in a background tab. Handing it to a CSS animation fails
        for the same underlying reason: while the rendering clock is not
        advancing the animation holds its first keyframe, so the computed opacity
        sits at the faded value instead of reaching 1.

        No fade is needed anyway. The bytes are already decoded by the time this
        runs, so assigning src paints the new photo in the same frame - there is
        no gap for a transition to cover. The feedback customers actually needed
        was the thumbnail highlighting instantly and the old photo never
        blanking, and both of those already happen.
      */
      mainImage.src = url;
      mainImage.style.opacity = '';        /* clear any inline value from before */
    };
    pre.onerror = function () {
      /* Still hand it to the browser rather than leaving the gallery stuck on
         the previous photo with a spinner spinning forever. */
      finish();
      mainImage.src = url;
      mainImage.style.opacity = '';
    };
    pre.src = url;
  }

  /*
    Thumbnail behaviour: select.

    Tapping a thumbnail swaps the hero image. Tapping the already-selected one
    used to open a full-size lightbox; that viewer has been removed, so a second
    tap now simply does nothing rather than re-loading the same photo.
  */
  document.querySelectorAll('.cp-gallery__thumb').forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      document.querySelectorAll('.cp-gallery__thumb').forEach(function (t) {
        t.classList.remove('is-active');
      });
      thumb.classList.add('is-active');
      showMainImage(thumb.dataset.full);

      /*
        Keep data-hires pointing at the photo now on screen.

        This sync also exists inside the magnifier block, but that block returns
        early when the magnifier setting is off - so with it off, data-hires kept
        the FIRST image's URL and anything reading it (the enlarge viewer below)
        showed the wrong photo after switching thumbnails. Doing it here means it
        is maintained regardless of the magnifier.
      */
      if (thumb.dataset.hires) mainImage.dataset.hires = thumb.dataset.hires;
    });
    thumb.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); thumb.click(); }
    });
  });

  /* Kept on window because other blocks on the page swap the hero image through
     this same load-then-show path rather than reimplementing it. */
  window.ctxShowMainImage = showMainImage;

  /* ── Thumbnail strip arrows ────────────────────────────────────────────
     The strip holds every product image now, so it can overflow. The arrows only
     exist when there is somewhere to go: hidden entirely for a product whose
     thumbnails all fit, and each one hidden at its own end of the scroll. */
  (function () {
    var strip = document.querySelector('.cp-gallery__thumbs');
    var wrap  = document.querySelector('.cp-gallery__thumbs-wrap');
    if (!strip || !wrap) return;
    var prev = wrap.querySelector('.cp-gallery__thumb-nav--prev');
    var next = wrap.querySelector('.cp-gallery__thumb-nav--next');
    if (!prev || !next) return;

    function pageWidth() {
      /*
        One full visible page per click.

        This used to be "three thumbnails wide", derived from the desktop row. On
        mobile the strip is a 2x2 grid laid out in columns, where three columns is
        one and a half pages - an arrow click would land mid-pair. Measuring the
        visible width instead means a page is a page in both layouts, and it stays
        correct if the thumbnail count per view ever changes.
      */
      var gap = parseFloat(getComputedStyle(strip).gap) || 0;
      return strip.clientWidth + gap;
    }

    function sync() {
      var scrollable = strip.scrollWidth - strip.clientWidth;
      if (scrollable <= 2) { prev.hidden = true; next.hidden = true; return; }
      prev.hidden = strip.scrollLeft <= 2;
      next.hidden = strip.scrollLeft >= scrollable - 2;
    }

    /* sync() is called directly as well as on the scroll event. With
       scroll-behavior: smooth the scroll event can lag the assignment, so relying
       on it alone left the arrows a beat behind the strip. */
    prev.addEventListener('click', function () { strip.scrollLeft -= pageWidth(); sync(); setTimeout(sync, 400); });
    next.addEventListener('click', function () { strip.scrollLeft += pageWidth(); sync(); setTimeout(sync, 400); });
    strip.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    /* Images load lazily, so scrollWidth is not final at parse time. */
    window.addEventListener('load', sync);
    sync();

    /* Keep the selected thumbnail in view when the gallery is driven from
       elsewhere - arrow keys in the lightbox, or closing it on a later image. */
    window.ctxRevealThumb = function (index) {
      var t = strip.querySelectorAll('.cp-gallery__thumb')[index];
      if (t && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };
  })();

  /* ── Quantity control ── */
  /* ── Quantity control ── */
  function updateAtcPrice() {
    var input  = document.getElementById('cp-qty-input');
    var atcBtn = document.querySelector('.cp-btn-atc');
    if (!input || !atcBtn || atcBtn.disabled) return;

    var qty        = parseInt(input.value, 10) || 1;
    var unitCents  = parseInt(atcBtn.dataset.unitPrice, 10);
    if (!unitCents) return;

    var label      = atcBtn.dataset.label || 'Add to bag';
    atcBtn.textContent = label + ' \u2014 ' + formatMoney(unitCents * qty);
  }

  window.cpChangeQty = function (delta) {
    var input = document.getElementById('cp-qty-input');
    if (!input) return;
    var val = parseInt(input.value, 10) + delta;
    if (val < 1)  val = 1;
    if (val > 99) val = 99;
    input.value = val;
    updateAtcPrice();
  };

/* ── Init ATC unit price on page load ── */
  (function initAtcUnitPrice() {
    var atcBtn = document.querySelector('.cp-btn-atc');
    if (!atcBtn || atcBtn.disabled) return;
    /* Parse the price out of the existing button text as fallback,
       but prefer reading from the hidden variant input */
    var priceEl = document.getElementById('cp-price-sale');
    if (priceEl) {
      /* cp-price-sale stores display string — re-parse cents from it */
      var raw = priceEl.textContent.replace(/[^0-9.]/g, '');
      var cents = Math.round(parseFloat(raw) * 100);
      if (cents) atcBtn.dataset.unitPrice = cents;
    }
    /* Also wire up manual typing in the qty input */
    var qtyInput = document.getElementById('cp-qty-input');
    if (qtyInput) {
      qtyInput.addEventListener('input', updateAtcPrice);
      qtyInput.addEventListener('change', updateAtcPrice);
    }
  })();

  /* ── Variant switching ── */
  var variants = window.__cp_variants || [];

  function findVariant(selections) {
    return variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === selections[i];
      });
    });
  }

  function updateSavedGymLink(variant) {
    if (!variant || !variant.id) return;
    document.querySelectorAll('[data-ctx-see-in-gym]').forEach(function (link) {
      var base = link.dataset.ctxBuilderUrl || '/pages/gym-bundle-builder';
      var handle = link.dataset.ctxBuilderHandle || '';
      if (!handle) return;
      link.href = base + (base.indexOf('?') > -1 ? '&' : '?') +
        'seed=' + encodeURIComponent(handle + '--v' + variant.id) + '#gym-builder';
    });
  }

  function updateVariantUI(variant) {
    if (!variant) return;

    /* hidden form input */
    var idInput = document.getElementById('cp-variant-id');
    if (idInput) idInput.value = variant.id;
    updateSavedGymLink(variant);

    /* Receive-by date. Stealth carries a different date to Ember, so the two
       places that print it have to move with the colour picker. Both are left
       exactly as the server rendered them when this variant has no date — the
       fallback wording ("Ships in 4-6 weeks") is not ours to reconstruct here. */
    (function () {
      var map = window.__cp_receiveby || {};
      var text = map[variant.id];
      if (!text) return;
      document.querySelectorAll('[data-cp-receiveby]').forEach(function (el) {
        el.textContent = (el.getAttribute('data-cp-receiveby-prefix') || '') + text;
      });
    })();

    /* price */
    var priceEl    = document.getElementById('cp-price-sale');
    var compareEl  = document.getElementById('cp-price-original');
    var atcBtn     = document.querySelector('.cp-btn-atc');
    var stockEl    = document.getElementById('cp-stock');
    var stockDot   = stockEl ? stockEl.querySelector('.cp-stock-dot') : null;

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    /* Overstock sale figures follow the variant. Same integer maths as the
       Liquid render (Liquid's divided_by on integers floors), so a variant
       switch can never show a figure the server would not have. */
    var saleRow = document.querySelector('[data-cp-sale-pct]');
    if (saleRow) {
      var salePct = parseInt(saleRow.getAttribute('data-cp-sale-pct'), 10) || 0;
      var saleNow = Math.floor(variant.price * (100 - salePct) / 100);
      var nowEl = saleRow.querySelector('[data-cp-sale-now]');
      var offEl = saleRow.querySelector('[data-cp-sale-off]');
      if (nowEl) nowEl.textContent = formatMoney(saleNow);
      if (offEl) offEl.textContent = 'Save ' + formatMoney(variant.price - saleNow);
    }
    if (compareEl) {
      /* window.CG_SHOW_COMPARE_AT mirrors the theme setting - see layout/theme.liquid.
         Without it, switching variant would re-show a was-price the server deliberately
         withheld. Defaults to hidden if the global is missing. */
      if (window.CG_SHOW_COMPARE_AT === true && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    /* ATC button */
    if (atcBtn) {
      var label = atcBtn.dataset.label || 'Add to bag';
      if (variant.available) {
        var qtyInput  = document.getElementById('cp-qty-input');
        var qty       = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        atcBtn.disabled = false;
        atcBtn.dataset.unitPrice = variant.price;
        atcBtn.textContent = label + ' \u2014 ' + formatMoney(variant.price * qty);
      } else {
        atcBtn.disabled    = true;
        atcBtn.dataset.unitPrice = '';
        atcBtn.textContent = window.variantStrings ? window.variantStrings.soldOut : 'Sold Out';
      }
    }

    /* gallery
       This is the part that went missing. The variant picker updates the hidden
       id, the price and the URL, but nothing ever swapped the photo - so picking
       the black colourway left the red machine on screen. Shopify gives each
       variant a featured_image when one is assigned in admin; variants without
       one keep whatever is showing rather than blanking the gallery. */
    var vImg = variant.featured_image || (variant.featured_media && variant.featured_media.preview_image);
    if (vImg && vImg.src) {
      var main = document.getElementById('cp-main-image');
      if (main) {
        var src = vImg.src;
        /* Liquid hands these over protocol-relative. */
        if (src.indexOf('//') === 0) src = window.location.protocol + src;
        var sized = src.replace(/(\.[a-z]+)(\?|$)/i, '_800x$1$2');
        main.src = sized;
        main.dataset.hires = src.replace(/(\.[a-z]+)(\?|$)/i, '_1600x$1$2');
        main.alt = vImg.alt || '';
      }
      /* Move the ring onto the matching thumbnail, matched on the filename so a
         width suffix in either URL does not break it. */
      var file = vImg.src.split('/').pop().split('?')[0].replace(/_\d+x(\.[a-z]+)$/i, '$1');
      document.querySelectorAll('.cp-gallery__thumb').forEach(function (t) {
        var tf = (t.dataset.full || '').split('/').pop().split('?')[0].replace(/_\d+x(\.[a-z]+)$/i, '$1');
        t.classList.toggle('is-active', tf === file);
      });
    }

    /* stock badge */
    if (stockEl && stockDot) {
      if (variant.available) {
        stockDot.classList.remove('cp-stock-dot--out');
        stockEl.textContent = '';
        stockEl.appendChild(stockDot);
        stockEl.appendChild(document.createTextNode(' In Stock'));
        stockEl.className = 'cp-stock-badge';
      } else {
        stockDot.classList.add('cp-stock-dot--out');
        stockEl.textContent = '';
        stockEl.appendChild(stockDot);
        stockEl.appendChild(document.createTextNode(' Out of Stock'));
        stockEl.className = 'cp-stock-badge cp-stock-badge--out';
      }
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── Wiring the picker ──
     This used to bind to `.cp-option-select`, a class the product page has not
     rendered since the picker moved to Shopify's own <variant-selects>. The
     selector matched nothing, so the handler never ran and the gallery never
     updated. Bound to what is actually on the page now, with the option-select
     path kept for any template still using it.

     The selection is resolved from the option controls themselves. The obvious
     alternative - waiting for the hidden variant id to change - does not work
     here: that id is written by a section re-render that goes to the network, so
     it can lag the click by seconds and is measurably still stale immediately
     after the event. The controls carry the answer straight away.

     Note `variant-selects.selectedOptionValues` is NOT usable for this: it
     returns option-value IDs, not the option names that variant.options holds. */
  function selectedOptionNames(root) {
    var vals = [];
    root.querySelectorAll('select').forEach(function (s) { vals.push(s.value); });
    root.querySelectorAll('input[type="radio"]:checked').forEach(function (r) { vals.push(r.value); });
    return vals;
  }

  function syncFromSelection() {
    var root = document.querySelector('variant-selects');
    var variant = null;

    if (root) variant = findVariant(selectedOptionNames(root));

    /* Fallbacks for a deep link, or any markup the above does not cover. */
    if (!variant) {
      var idInput = document.getElementById('cp-variant-id');
      var id = (idInput && idInput.value) || new URLSearchParams(window.location.search).get('variant');
      if (id) variant = variants.find(function (v) { return v.id === parseInt(id, 10); });
    }

    if (variant) updateVariantUI(variant);
  }

  /* Delegated from the document rather than bound to <variant-selects>.
     Selecting a variant triggers a section re-render that swaps that element out
     for a fresh one, taking any listener attached to it with it - so a direct
     binding works exactly once and then goes quiet. Measured: the first pick
     updated the photo, every pick after it did nothing. */
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest && e.target.closest('variant-selects')) syncFromSelection();
  });

  document.querySelectorAll('.cp-option-select').forEach(function (select) {
    select.addEventListener('change', function () {
      var selections = [];
      document.querySelectorAll('.cp-option-select').forEach(function (s) {
        selections.push(s.value);
      });
      var variant = findVariant(selections);
      if (variant) updateVariantUI(variant);
    });
  });

  /* Landing on a ?variant= link should show that variant's photo, not the
     product's default one. */
  if (document.querySelector('variant-selects') || new URLSearchParams(window.location.search).get('variant')) {
    syncFromSelection();
  }

  /* ── Bundle AJAX add-to-cart ── */
  window.cpAddBundle = function (btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled    = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.textContent = '✓ Added';
        /* Refresh Dawn cart drawer */
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(function () {
          btn.innerHTML = 'Add <svg width="12" height="12" fill="none" viewBox="0 0 24 24"><path stroke="#fff" stroke-width="2.5" stroke-linecap="round" d="M12 5v14M5 12h14"/></svg>';
          btn.disabled  = false;
        }, 2000);
      })
      .catch(function () {
        btn.textContent = 'Error';
        btn.disabled    = false;
      });
  };

  /* ── Spec tooltip ── */
  var tooltip   = document.getElementById('cp-tooltip');
  var tooltipTx = document.getElementById('cp-tooltip-text');
  var tooltipAr = document.getElementById('cp-tooltip-arrow');
  var tooltipCl = document.getElementById('cp-tooltip-close');
  var activeBtn = null;

  function openTooltip(btn) {
    if (!tooltip || !tooltipTx) return;
    if (activeBtn) activeBtn.classList.remove('is-active');
    activeBtn = btn;
    btn.classList.add('is-active');

    tooltipTx.textContent = btn.dataset.tooltip;

    var rect = btn.getBoundingClientRect();
    var pw   = 260;
    var gap  = 12;
    var left = rect.right + gap;
    var top  = rect.top + window.scrollY - 8;

    if (left + pw > window.innerWidth - 12) {
      left = rect.left - pw - gap;
      tooltipAr.style.right = '-5px';
      tooltipAr.style.left  = 'auto';
    } else {
      tooltipAr.style.left  = '-5px';
      tooltipAr.style.right = 'auto';
    }
    tooltipAr.style.top = '14px';

    tooltip.style.left = left + 'px';
    tooltip.style.top  = top + 'px';
    tooltip.classList.add('is-visible');
  }

  function closeTooltip() {
    if (!tooltip) return;
    tooltip.classList.remove('is-visible');
    if (activeBtn) { activeBtn.classList.remove('is-active'); activeBtn = null; }
  }

  document.querySelectorAll('.cp-spec-info').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (activeBtn === btn) { closeTooltip(); return; }
      openTooltip(btn);
    });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); }
    });
  });

  if (tooltipCl) tooltipCl.addEventListener('click', closeTooltip);
  document.addEventListener('click', function (e) {
    if (tooltip && tooltip.classList.contains('is-visible') && !tooltip.contains(e.target)) {
      closeTooltip();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeTooltip();
  });

})();

/* ── Enlarge Image Viewer ──
   Standalone on purpose. It lives ABOVE the magnifier block and shares nothing
   with it: no pointer gate, no dependence on .cp-magnifier-wrap. The lens is a
   mouse-only nicety that is currently off; enlarging a photo matters most on the
   phone, so the two must not share an early return the way they used to. */
(function () {
  var img  = document.getElementById('cp-main-image');
  var view = document.getElementById('cp-imgview');
  if (!img || !view) return;

  var big      = document.getElementById('cp-imgview-img');
  var stage    = view.querySelector('[data-cp-imgview-stage]');
  var closeBtn = view.querySelector('[data-cp-imgview-close]');
  if (!big) return;

  var teardown = null;
  var lastFocus = null;

  function open() {
    /* data-hires is the 1600px render and is kept current by the thumbnail
       handler; currentSrc covers the case where it is somehow missing. */
    big.src = img.dataset.hires || img.currentSrc || img.src;
    big.alt = img.alt || '';

    lastFocus = document.activeElement;
    view.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    if (typeof cpPinToVisualViewport === 'function') teardown = cpPinToVisualViewport(view);

    /* Focusing on a coarse pointer can make the browser scroll the overlay to
       chase the control - same reasoning as the spec modals. */
    if (closeBtn && !window.matchMedia('(pointer: coarse)').matches) {
      setTimeout(function () { closeBtn.focus(); }, 40);
    }
  }

  function close() {
    if (view.hasAttribute('hidden')) return;
    view.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (teardown) { teardown(); teardown = null; }

    /* Drop the source so a large photo is not held in memory, and so the next
       open cannot flash the previous product's image before the new one decodes. */
    big.removeAttribute('src');

    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  img.addEventListener('click', open);

  /* Close on the backdrop or the button, but NOT on the photo itself - tapping
     the image is how people pinch and pan it. */
  view.addEventListener('click', function (e) {
    if (e.target === view || e.target === stage ||
        (e.target.closest && e.target.closest('[data-cp-imgview-close]'))) {
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();

/* ── Image Magnifier ── */
(function () {
  var wrap  = document.querySelector('.cp-magnifier-wrap');
  if (!wrap) return; // magnifier disabled in settings

  /*
    Pointer gate. The note further down says touch is deliberately not wired to
    the magnifier - but that was only true of the touch listeners. iOS Safari
    SYNTHESISES mouse events on tap, so `mousemove` fired anyway, the lens
    engaged, and because a tap never produces a mouseleave it stayed engaged.
    The result on an iPhone was a product page showing a hugely magnified crop of
    the photo with no way back, which read as a blank page. Android was fine.

    Caught from a customer video on 2026-08-03, hours after the magnifier was
    switched on. Binding nothing at all on touch is the fix; a mouseleave reset
    would not help, because no such event arrives.
  */
  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var img   = document.getElementById('cp-main-image');
  var lens  = document.getElementById('cp-magnifier-lens');
  if (!img || !lens) return;

  /* Read settings from data attributes */
  var zoom      = parseFloat(wrap.dataset.zoom)     || 2.5;
  var lensSize  = parseInt(wrap.dataset.lensSize)   || 160;
  var shape     = wrap.dataset.lensShape            || 'circle';
  var crosshair = wrap.dataset.showCrosshair === 'true';

  /* Apply shape & crosshair classes */
  if (shape === 'square') lens.classList.add('is-square');
  if (crosshair)          lens.classList.add('has-crosshair');

  /* Set lens dimensions */
  lens.style.width  = lensSize + 'px';
  lens.style.height = lensSize + 'px';

  /*
    The "Hover to zoom" magnifying-glass badge is deliberately gone.

    Clicking or tapping the image now opens the full-size viewer on every device,
    and that is the zoom affordance - the cursor turns to zoom-in on a pointer,
    and on touch the viewer supports pinch. The badge only ever described the
    desktop hover lens, was meaningless on a phone, and sat on top of the product
    photo competing with the condition badge for the same corner.

    ── STALE AS OF 2026-08-03 ──
    Everything above still describes the intent, but the full-size viewer is NOT
    currently reachable. It is wired up inside this IIFE, which returns early when
    the magnifier setting is off, and the setting was turned off after the lens
    stuck on iPhones (iOS synthesises mousemove on tap, with no mouseleave). So
    turning the magnifier off silently took tap-to-enlarge with it - on every
    device, including the phones that never had the lens.

    The viewer and the lens are two separate features sharing one early return.
    Lifting the viewer above the pointer gate would give tap-to-enlarge back
    without re-enabling the hover lens. Until then #cp-main-image uses a plain
    cursor, because the zoom-in cursor was promising a click that does nothing.
  */

  /* Load high-res image into lens background */
  function loadHires() {
    var hires = img.dataset.hires || img.src;
    lens.style.backgroundImage = 'url(' + hires + ')';
    updateBgSize();
  }

  function updateBgSize() {
    var naturalW = img.naturalWidth  || img.width;
    var naturalH = img.naturalHeight || img.height;
    var rect = img.getBoundingClientRect();
    /* background-size = rendered size × zoom */
    var bw = rect.width  * zoom;
    var bh = rect.height * zoom;
    lens.style.backgroundSize = bw + 'px ' + bh + 'px';
  }

  function moveLens(e) {
    var rect  = wrap.getBoundingClientRect();
    var imgRect = img.getBoundingClientRect();

    /* Mouse position relative to the image */
    var mx = (e.touches ? e.touches[0].clientX : e.clientX) - imgRect.left;
    var my = (e.touches ? e.touches[0].clientY : e.clientY) - imgRect.top;

    /* Clamp so lens stays inside image */
    var half = lensSize / 2;
    mx = Math.max(half / zoom, Math.min(imgRect.width  - half / zoom, mx));
    my = Math.max(half / zoom, Math.min(imgRect.height - half / zoom, my));

    /* Position lens centred on cursor, relative to wrap */
    var lx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left - half;
    var ly = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top  - half;

    /* Clamp lens inside wrap */
    lx = Math.max(0, Math.min(rect.width  - lensSize, lx));
    ly = Math.max(0, Math.min(rect.height - lensSize, ly));

    lens.style.left = lx + 'px';
    lens.style.top  = ly + 'px';

    /* Background position: mirror cursor position scaled by zoom */
    var bx = mx * zoom - half;
    var by = my * zoom - half;
    lens.style.backgroundPosition = '-' + bx + 'px -' + by + 'px';
  }

  function showLens() {
    updateBgSize();
    lens.style.display = 'block';
    /* Lets CSS clear the badge and gallery controls out of the way. Needed for
       touch, where :hover never fires. */
    wrap.classList.add('is-zooming');
  }

  function hideLens() {
    lens.style.display = 'none';
    wrap.classList.remove('is-zooming');
  }

  /* Load hires immediately and on image change */
  if (img.complete) {
    loadHires();
  } else {
    img.addEventListener('load', loadHires);
  }

  /* Re-init when thumbnail switches image */
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.attributeName === 'src') {
        hideLens();
        setTimeout(loadHires, 50);
      }
    });
  });
  observer.observe(img, { attributes: true, attributeFilter: ['src'] });

  /* Also update when data-hires changes (set by gallery thumb switcher) */
  document.querySelectorAll('.cp-gallery__thumb').forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      var hires = thumb.dataset.hires;
      if (hires) img.dataset.hires = hires;
    });
  });

  wrap.addEventListener('mouseenter', showLens);
  wrap.addEventListener('mouseleave', hideLens);
  wrap.addEventListener('mousemove',  moveLens);

  /*
    Touch is deliberately NOT wired to the magnifier.

    It used to be, via passive:true listeners. Passive listeners cannot call
    preventDefault, so dragging a finger across the image fired the lens AND
    scrolled the page at the same time - the lens chased the finger while the
    content moved under it. Making them non-passive and calling preventDefault
    would fix the double action but then swallow vertical scrolling over the
    largest element on the page, which is worse.

    A magnifier is a pointer affordance anyway: it needs a cursor position that
    is not also the thing doing the scrolling. On touch, the image simply
    scrolls, and tapping a thumbnail still switches the main image.
  */

  /* Re-calculate on window resize */
  window.addEventListener('resize', updateBgSize);

})();


/* ══════════════════════════════════════════════════════════════
   LOAD A POPUP'S VIDEO ONLY ONCE THE POPUP IS OPEN
   ══════════════════════════════════════════════════════════════

   YouTube decides which preview image to serve by measuring the player when the
   iframe loads. Load it inside a display:none popup and it measures nothing, picks
   the smallest still it has, and never upgrades - the video looked pixelated for
   exactly that reason, on a source image that is perfectly sharp.

   So the markup carries data-src and nothing loads until the popup is open and the
   player has real dimensions. Closing clears src again, which stops playback and
   means the next open re-measures.

   Worth having anyway: a product page with five help popups was quietly loading five
   YouTube players nobody had asked for. */
function cpActivateModalVideos(modal) {
  if (!modal) return;
  modal.querySelectorAll('iframe[data-src]').forEach(function (f) {
    var fill = function () {
      if (!f.getAttribute('src')) f.setAttribute('src', f.getAttribute('data-src'));
    };
    /* Two frames of grace so layout has settled and the player measures the panel
       at full size rather than mid-transition. */
    requestAnimationFrame(function () { requestAnimationFrame(fill); });
    /* Belt and braces: animation frames are throttled to a standstill whenever
       the page is not being painted - a backgrounded tab, low power mode, some
       in-app browsers. When that happens the callback above simply never runs and
       the panel shows an empty black frame where the film should be. This timer
       is not throttled the same way, and fill() is a no-op if the frames did run. */
    setTimeout(fill, 300);
  });
}

function cpDeactivateModalVideos(modal) {
  if (!modal) return;
  modal.querySelectorAll('iframe[data-src]').forEach(function (f) {
    f.removeAttribute('src');
  });
}

/* ── Step Modals ── */
/* Same visual-viewport pinning as the spec popups - see cpPinToVisualViewport
   below. The shipping calculator opens through this path too, which is why it had
   the identical off-screen behaviour. */
var cpStepModalTeardowns = {};

function cpOpenStepModal(id) {
  var modal = document.getElementById(id);
  if (!modal) return;
  modal.removeAttribute('hidden');
  document.body.style.overflow = 'hidden';
  cpStepModalTeardowns[id] = cpPinToVisualViewport(modal);
  cpActivateModalVideos(modal);
  if (!window.matchMedia('(pointer: coarse)').matches) {
    var closeBtn = modal.querySelector('.cp-step-modal__close');
    if (closeBtn) setTimeout(function(){ closeBtn.focus(); }, 50);
  }
}

function cpCloseStepModal(id) {
  var modal = document.getElementById(id);
  if (!modal) return;
  modal.setAttribute('hidden', '');
  document.body.style.overflow = '';
  if (cpStepModalTeardowns[id]) { cpStepModalTeardowns[id](); delete cpStepModalTeardowns[id]; }
  cpDeactivateModalVideos(modal);
}

document.addEventListener('keydown', function(e) {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.cp-step-modal:not([hidden])').forEach(function(m){
    cpCloseStepModal(m.id);
  });
});

/* ══════════════════════════════════════════════════════════════
   PIN A MODAL TO THE VISUAL VIEWPORT
   ══════════════════════════════════════════════════════════════

   position:fixed pins an element to the LAYOUT viewport. Pinch-zoom changes the
   VISUAL viewport, and the two are not the same: zoom in, and a fixed overlay
   keeps its layout coordinates, so it lands partly or entirely off the visible
   area and drifts around as you pan. That is the "it popped out and I can move
   around" behaviour on these popups - and it became reachable the moment
   pinch-zoom was enabled on product media.

   window.visualViewport reports what the customer can actually see, zoom and pan
   included. Sizing the overlay from it on open, and following its resize and
   scroll events, keeps the popup filling the visible area whatever the zoom.

   Returns a teardown function; call it on close so the listeners do not
   accumulate one pair per open.
================================================================ */
function cpPinToVisualViewport(el) {
  var vv = window.visualViewport;
  if (!vv || !el) return function () {};

  /*
    Only trust a viewport with real dimensions.

    Some embedded/headless browser contexts report visualViewport as 0x0. Copying
    that onto the overlay collapsed it to nothing - the popup was technically open
    and completely invisible. A guard is cheap, and the fallback is the stylesheet's
    own inset:0, which is correct at zoom 1 and merely imperfect when zoomed.
  */
  function usable() {
    return vv.width > 0 && vv.height > 0;
  }

  function apply() {
    if (!usable()) {
      /* Hand sizing back to CSS rather than pinning to nonsense. */
      el.style.position = el.style.left = el.style.top = '';
      el.style.width = el.style.height = el.style.right = el.style.bottom = '';
      return;
    }
    el.style.position = 'fixed';
    el.style.left   = vv.offsetLeft + 'px';
    el.style.top    = vv.offsetTop + 'px';
    el.style.width  = vv.width + 'px';
    el.style.height = vv.height + 'px';
    el.style.right  = 'auto';
    el.style.bottom = 'auto';
  }

  apply();
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);

  return function teardown() {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    /* Hand sizing back to the stylesheet so a later open starts clean. */
    el.style.position = '';
    el.style.left = el.style.top = el.style.width = el.style.height = '';
    el.style.right = el.style.bottom = '';
  };
}

/* ── Shipping calculator pop-out ──
   The Shipping spec row opens the calculator instead of just stating the
   options. The calculator is MOVED here rather than copied: its script binds to
   one `[data-cp-shipcalc]` element, so a second copy would look right and never
   calculate. A placeholder marks the spot it came from so it goes back exactly
   where it was, with any typed ZIP and any result still on it. */
(function () {
  var modal, body, calc, placeholder, teardown;

  function open() {
    modal = modal || document.getElementById('cp-calc-modal');
    body = body || document.getElementById('cp-calc-modal-body');
    calc = document.querySelector('[data-cp-shipcalc]');
    if (!modal || !body || !calc) return;

    if (!placeholder) {
      placeholder = document.createComment('cp-shipcalc-home');
      calc.parentNode.insertBefore(placeholder, calc);
    }
    body.appendChild(calc);
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    if (typeof cpPinToVisualViewport === 'function') teardown = cpPinToVisualViewport(modal);

    var firstField = calc.querySelector('select, input');
    if (firstField && !window.matchMedia('(pointer: coarse)').matches) {
      setTimeout(function () { firstField.focus(); }, 60);
    }
  }

  function close() {
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (teardown) { teardown(); teardown = null; }
    /* Put it back. If the placeholder has gone - a section re-render, say - leave
       the calculator in the panel rather than dropping it out of the document. */
    if (calc && placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(calc, placeholder);
    }
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-cp-open-calc]')) { e.preventDefault(); open(); return; }
    if (e.target.closest && e.target.closest('[data-cp-close-calc]')) { close(); }
  });

  /* The row is a div with role=button, so it has to handle keys itself. */
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest('[data-cp-open-calc]')) {
      e.preventDefault();
      open();
    }
    if (e.key === 'Escape') close();
  });
})();

/* ── Spec Tooltip Modals ── */
var cpSpecModalTeardowns = {};

function cpOpenSpecModal(id) {
  var modal = document.getElementById(id);
  if (!modal) return;
  modal.removeAttribute('hidden');
  document.body.style.overflow = 'hidden';
  cpSpecModalTeardowns[id] = cpPinToVisualViewport(modal);
  cpActivateModalVideos(modal);

  /* Focusing on a coarse pointer can make the browser scroll the overlay to
     chase the focused control; keyboard users still get focus moved in. */
  if (!window.matchMedia('(pointer: coarse)').matches) {
    var closeBtn = modal.querySelector('.cp-spec-modal__close');
    if (closeBtn) setTimeout(function(){ closeBtn.focus(); }, 50);
  }
}
function cpCloseSpecModal(id) {
  var modal = document.getElementById(id);
  if (!modal) return;
  modal.setAttribute('hidden', '');
  document.body.style.overflow = '';
  if (cpSpecModalTeardowns[id]) { cpSpecModalTeardowns[id](); delete cpSpecModalTeardowns[id]; }
  cpDeactivateModalVideos(modal);
}
document.addEventListener('keydown', function(e) {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.cp-spec-modal:not([hidden])').forEach(function(m){
    cpCloseSpecModal(m.id);
  });
});

/* ── Description Read More — scroll to Product Details tabs ── */
(function () {
  var readMore = document.getElementById('cp-read-more');
  if (!readMore) return;

  readMore.addEventListener('click', function () {
    var tabsSection = document.querySelector('.product-details-tabs');
    if (tabsSection) {
      tabsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
})();


/* ── Video posters ────────────────────────────────────────────────────────
   Swaps the still for the real player on click. Delegated, because these
   posters live inside popups that are re-rendered by the theme editor. */
document.addEventListener('click', function (e) {
  var poster = e.target.closest && e.target.closest('[data-cp-video-facade]');
  if (!poster) return;
  var src = poster.getAttribute('data-src');
  if (!src) return;
  var frame = document.createElement('iframe');
  frame.className = 'cp-spec-modal__video';
  frame.setAttribute('frameborder', '0');
  frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
  frame.setAttribute('allowfullscreen', '');
  frame.setAttribute('title', poster.getAttribute('aria-label') || 'Video');
  frame.src = src;
  poster.replaceWith(frame);
});

/* ── Overstock code: tap to copy ─────────────────────────────────────────
   Delegated, so it survives section re-renders. The clipboard call sits
   inside the click itself, which is what browsers require; if they refuse,
   the code is selected instead so a long-press copy still works. The label
   flips to "Copied" for two seconds either way the customer gets the code. */
document.addEventListener('click', function (e) {
  var chip = e.target.closest && e.target.closest('[data-cp-copy-code]');
  if (!chip) return;
  var code = chip.getAttribute('data-cp-copy-code');
  var label = chip.querySelector('[data-cp-copy-label]');
  function done(text) {
    if (!label) return;
    label.textContent = text;
    clearTimeout(chip._cpCopyT);
    chip._cpCopyT = setTimeout(function () { label.textContent = 'Copy'; }, 2000);
  }
  function selectFallback() {
    try {
      var range = document.createRange();
      range.selectNodeContents(chip.firstChild || chip);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (err) {}
    done('Selected');
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code).then(function () { done('Copied'); }, selectFallback);
  } else {
    selectFallback();
  }
});
